/**
 * API: Asistente Formulador (v1.1.0 slice 1 + slice 2 preparacion).
 *
 * De una idea en lenguaje natural a los 6 modulos de identificacion de la
 * MGA, data-grounded sobre los datos reales del muni. El backend valida
 * tier Pro + cuota IA en el endpoint `/draft`; en 402 lanza ApiError con
 * `detailData.reason` (`tier_locked_pro_required` | `ai_quota_exhausted`).
 *
 * Slice 2 (ADR-021): 3 modulos de preparacion (cadena_valor, presupuesto,
 * cronograma), gated server-side hasta que los 6 de identificacion esten
 * `confirmed` (409 `{reason: "identificacion_incompleta"}` si no).
 *
 * Contrato: docs/superpowers/specs/2026-06-24-asistente-formulador-design.md
 * y docs/superpowers/specs/2026-07-07-formulador-slice2-preparacion-design.md
 */

import { request } from "./_client";

export type ModuleKey =
  | "problema"
  | "causas_efectos"
  | "poblacion"
  | "objetivo"
  | "alternativas"
  | "productos"
  | "cadena_valor"
  | "presupuesto"
  | "cronograma";

/** 'empty' = aun sin redactar; 'draft' = borrador; 'confirmed' = confirmado por el humano. */
export type ModuleStatus = "empty" | "draft" | "confirmed";

/** 'empty' = sin contenido; 'ai' = sugerido por IA; 'edited' = editado por el usuario. */
export type ModuleOrigin = "empty" | "ai" | "edited";

export interface ModuleDTO {
  key: string;
  content: string;
  status: ModuleStatus;
  origin: ModuleOrigin;
  /** Supuestos que la IA marco como inferencia (no dato duro del muni). */
  assumptions: string[];
}

export interface FormulationDTO {
  id: string;
  project_id: string;
  idea_text: string;
  /** 'in_progress' | 'done'. */
  status: string;
  modules: Record<string, ModuleDTO>;
}

export interface DraftModuleResponseDTO {
  module: ModuleDTO;
  /** Usos de IA restantes este mes despues de esta redaccion. */
  quota_remaining: number;
}

/**
 * Orden de construccion de los 6 modulos de identificacion.
 *
 * IMPORTANTE: esta constante NO se extiende con los modulos de preparacion
 * (slice 2) — el codigo existente la usa para contar "X de 6 confirmados"
 * (`ConfirmedProgressBar` en `FormulationDocument`) y ese conteo debe seguir
 * siendo solo sobre identificacion. Los 3 modulos nuevos viven en
 * `PREPARACION_ORDER`, una lista separada.
 */
export const MODULE_ORDER: ModuleKey[] = [
  "problema",
  "causas_efectos",
  "poblacion",
  "objetivo",
  "alternativas",
  "productos",
];

/**
 * Orden de construccion de los 3 modulos de preparacion (slice 2, ADR-021).
 * Gated: server-side devuelve 409 si se redactan/editan antes de que los 6
 * de `MODULE_ORDER` esten `confirmed` (ver `isIdentificacionConfirmada`).
 */
export const PREPARACION_ORDER: ModuleKey[] = ["cadena_valor", "presupuesto", "cronograma"];

/** Etiqueta + pregunta base por modulo, en lenguaje del secretario. */
export const MODULE_META: Record<ModuleKey, { label: string; question: string }> = {
  problema: {
    label: "Problema central",
    question: "¿Qué problema concreto quieres resolver?",
  },
  causas_efectos: {
    label: "Causas y efectos",
    question: "¿Por qué ocurre y qué consecuencias tiene?",
  },
  poblacion: {
    label: "Población",
    question: "¿A quiénes afecta y cuántas personas son?",
  },
  objetivo: {
    label: "Objetivo",
    question: "¿Qué situación quieres lograr (objetivo general y específicos)?",
  },
  alternativas: {
    label: "Alternativas",
    question: "¿Qué opciones hay, y cuál eliges y por qué?",
  },
  productos: {
    label: "Productos",
    question: "¿Qué entregará el proyecto y cómo se mide?",
  },
  cadena_valor: {
    label: "Cadena de valor",
    question: "¿Qué actividades necesita cada producto?",
  },
  presupuesto: {
    label: "Presupuesto",
    question: "¿Cuánto costaría un proyecto así?",
  },
  cronograma: {
    label: "Cronograma",
    question: "¿En qué orden y cuánto tarda cada fase?",
  },
};

/**
 * True si los 6 modulos de identificacion estan `confirmed` — espejo en FE
 * de `identificacion_confirmada` (backend, `project_formulator/formulations.py`).
 * Usado para decidir si la seccion "Preparación" se muestra bloqueada o
 * desbloqueada (ADR-021 regla 5), y por la UI para no intentar acciones que
 * el backend va a rechazar con 409.
 */
export function isIdentificacionConfirmada(formulation: FormulationDTO): boolean {
  return MODULE_ORDER.every((key) => formulation.modules[key]?.status === "confirmed");
}

/** Crea un expediente idea-first + su formulacion con 6 modulos vacios. */
export function createFormulation(municipality: string, ideaText: string): Promise<FormulationDTO> {
  return request<FormulationDTO>("/formulations", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ municipality, idea_text: ideaText }),
  });
}

/** Lee una formulacion (tenant-scoped en el backend; cross-tenant -> 404). */
export function getFormulation(
  formulationId: string,
  municipality: string,
): Promise<FormulationDTO> {
  return request<FormulationDTO>(
    `/formulations/${encodeURIComponent(formulationId)}?municipality=${encodeURIComponent(municipality)}`,
  );
}

/**
 * Lee la formulacion de un expediente por su `project_id` (para reabrir desde
 * la lista de Expedientes, que solo conoce el project_id). 404 si no existe.
 */
export function getFormulationByProject(
  projectId: string,
  municipality: string,
): Promise<FormulationDTO> {
  return request<FormulationDTO>(
    `/formulations/by-project/${encodeURIComponent(projectId)}?municipality=${encodeURIComponent(municipality)}`,
  );
}

/** Edita o confirma un modulo (origen pasa a 'edited'). */
export function updateFormulationModule(args: {
  formulationId: string;
  moduleKey: string;
  municipality: string;
  content: string;
  status: "draft" | "confirmed";
}): Promise<FormulationDTO> {
  return request<FormulationDTO>(
    `/formulations/${encodeURIComponent(args.formulationId)}/modules/${encodeURIComponent(args.moduleKey)}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        municipality: args.municipality,
        content: args.content,
        status: args.status,
      }),
    },
  );
}

/**
 * La IA redacta (o ajusta) un modulo. Gated por tier Pro + cuota IA; en 402
 * el caller debe leer `detailData.reason` del ApiError para el upsell.
 */
export function draftFormulationModule(args: {
  formulationId: string;
  moduleKey: string;
  municipality: string;
  userMessage?: string;
}): Promise<DraftModuleResponseDTO> {
  return request<DraftModuleResponseDTO>(
    `/formulations/${encodeURIComponent(args.formulationId)}/modules/${encodeURIComponent(args.moduleKey)}/draft`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        municipality: args.municipality,
        user_message: args.userMessage ?? null,
      }),
    },
  );
}
